import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { HomeSkeleton } from './PageSkeletons';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import {
  Building2,
  Users,
  IndianRupee,
  TrendingUp,
  Bell,
  Plus,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Sparkles,
  Flame,
  MessageCircle,
  Home as HomeIcon,
  ChevronDown,
  ChevronUp,
  BedDouble,
  UserMinus,
  CalendarClock,
  Wallet,
  Receipt,
  Coins,
  TrendingDown,
  ShieldCheck,
  Copy,
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { EVT } from '../../services/dataSyncBus';
import { useDataSync } from '../../hooks/useDataSync';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import { LastSyncedBadge } from './LastSyncedBadge';
import { track, peopleSet } from '../../services/analytics';

// Revenue milestones (in INR). When a manager crosses one, fire once and stamp on profile.
const MILESTONES = [50000, 100000, 250000, 500000, 1000000, 2500000, 5000000];
const milestoneKey = (v) => `rk_milestone_${v}`;
const fireMilestoneIfCrossed = (lifetimeRevenue) => {
  if (typeof window === 'undefined') return;
  for (const m of MILESTONES) {
    if (lifetimeRevenue >= m && !localStorage.getItem(milestoneKey(m))) {
      localStorage.setItem(milestoneKey(m), '1');
      track('revenue_milestone_crossed', { milestone_inr: m, lifetime_revenue: lifetimeRevenue });
      peopleSet({ highest_milestone_inr: m });
    }
  }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

// 10 KPI tiles — first 6 visible, last 4 behind "More".
// `route` is where clicking the card takes the user; `query` is appended for
// pages that support pre-filtering (e.g. defaulters list on the Dues page).
const KPI_TILES = [
  { key: 'today_collection', label: "Today's Collection", icon: Coins,        kind: 'money', tone: 'success', route: '/manager/money/collections', query: '?range=today' },
  { key: 'month_collection', label: "This Month's Collection", icon: Wallet,  kind: 'money', tone: 'success', route: '/manager/money/collections', query: '?range=month' },
  { key: 'total_dues',       label: 'Total Dues',         icon: AlertCircle,  kind: 'money', tone: 'danger',  route: '/manager/money/dues' },
  { key: 'rent_defaulters',  label: 'Rent Defaulters',    icon: UserMinus,    kind: 'count', tone: 'danger',  route: '/manager/money/dues', query: '?filter=defaulters' },
  { key: 'month_expenses',   label: "This Month's Expenses", icon: Receipt,   kind: 'money', tone: 'warn',    route: '/manager/money/expenses' },
  { key: 'current_deposit',  label: 'Current Deposit',    icon: ShieldCheck,  kind: 'money', tone: 'success', route: '/manager/money/profit-loss' },
  // Behind "More"
  { key: 'month_dues',       label: "This Month's Dues",  icon: AlertCircle,  kind: 'money', tone: 'danger',  hidden: true, route: '/manager/money/dues',         query: '?range=month' },
  { key: 'unpaid_deposit',   label: 'Unpaid Deposit',     icon: ShieldCheck,  kind: 'money', tone: 'danger',  hidden: true, route: '/manager/money/dues',         query: '?filter=unpaid_deposit' },
  { key: 'month_profit',     label: "This Month's Profit",icon: TrendingUp,   kind: 'money', tone: 'success', hidden: true, route: '/manager/money/profit-loss' },
  { key: 'potential_rent',   label: 'Potential Rent',     icon: TrendingDown, kind: 'money', tone: 'neutral', hidden: true, route: '/manager/property/list' },
];

const Home = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [home, setHome] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [hideTest, setHideTest] = useState(() => {
    try { return localStorage.getItem('rk_hide_test') !== 'false'; }
    catch { return true; }
  });
  const [recentPayments, setRecentPayments] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [insights, setInsights] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, homeRes, paymentsRes, insightsRes, funnelRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { overview: null } })),
        axios.get(`${API}/analytics/home-summary`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { include_test: !hideTest },
        }).catch(() => ({ data: null })),
        axios.get(`${API}/money/payments`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/ai/insights/home`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/leads/funnel/summary?days=30`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
      ]);

      setStats(analyticsRes.data?.overview || null);
      setHome(homeRes.data);
      setRecentPayments((paymentsRes.data || []).slice(0, 5));
      setInsights(insightsRes.data);
      setFunnel(funnelRes.data);
      setLastSyncedAt(new Date());
      // Fire revenue milestone & profile size metrics once per page-load
      const lifetimeRev = insightsRes.data?.lifetime_revenue
        || insightsRes.data?.total_revenue
        || insightsRes.data?.this_month_collection?.total
        || 0;
      if (lifetimeRev > 0) fireMilestoneIfCrossed(lifetimeRev);
      peopleSet({
        properties_count:    insightsRes.data?.properties_count    || 0,
        active_tenants:      insightsRes.data?.active_tenants      || 0,
        beds_total:          insightsRes.data?.beds_total          || 0,
        last_home_loaded_at: new Date().toISOString(),
      });

      setPendingTasks([
        { id: 1, title: 'Review new tenant application', priority: 'high' },
        { id: 2, title: 'Property maintenance scheduled', priority: 'medium' },
        { id: 3, title: 'Collect rent from Room 205', priority: 'high' }
      ]);
    } catch (error) {
      console.error('Error fetching home data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, hideTest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Home shows the most aggregate numbers, so it subscribes to EVERY mutation
  // type. Debounced internally so a chain of related events (e.g. move-in fires
  // 5 events at once) collapses into a single refetch.
  useDataSync(
    [EVT.PAYMENT, EVT.TENANT, EVT.BOOKING, EVT.EXPENSE, EVT.ROOM, EVT.PROPERTY, EVT.LEAD],
    fetchData,
    { debounceMs: 350 },
  );
  useRefetchOnFocus(fetchData);

  const toggleHideTest = () => {
    const next = !hideTest;
    setHideTest(next);
    try { localStorage.setItem('rk_hide_test', String(next)); } catch { /* ignore */ }
  };

  if (loading) {
    return <HomeSkeleton />;
  }

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
      {/* Welcome Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">
            Welcome back, {user?.name || 'Manager'}! 👋
          </h1>
          <p className="text-gray-600 mt-2">Here's what's happening with your properties today.</p>
        </div>
        <div className="flex gap-3 items-center">
          <LastSyncedBadge syncedAt={lastSyncedAt} onRefresh={fetchData} refreshing={loading} />
          <Button
            onClick={() => { track('cta_add_tenant_clicked', { surface: 'home' }); navigate('/manager/people/tenants'); }}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="add-tenant-btn"
          >
            <Plus size={20} className="mr-2" />
            Add Tenant
          </Button>
          <Button
            onClick={() => { track('cta_record_payment_clicked', { surface: 'home' }); navigate('/manager/money'); }}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="record-payment-btn"
          >
            <Plus size={20} className="mr-2" />
            Record Payment
          </Button>
        </div>
      </div>

      {/* === Month summary KPI strip === */}
      {home && (
        <MonthSummaryStrip
          monthLabel={home.month_label}
          summary={home.month_summary}
          showMore={showMore}
          onToggle={() => setShowMore(v => !v)}
          propertiesCount={home.properties?.length || 0}
          hideTest={hideTest}
          onToggleTest={toggleHideTest}
        />
      )}

      {/* === Per-property breakdown cards === */}
      {home && home.properties?.length > 0 && (
        <PropertyBreakdownStrip properties={home.properties} navigate={navigate} />
      )}

      {/* === AI Command Center === */}
      {insights && (
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-950 via-purple-900 to-slate-900 p-6 shadow-2xl border border-purple-500/30" data-testid="ai-command-center">
          {/* Background sparkles */}
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-4 right-4 text-6xl">✨</div>
            <div className="absolute bottom-8 left-10 text-4xl">⚡</div>
          </div>

          <div className="relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg">
                  <Sparkles className="text-white" size={20} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">AI Command Center</h2>
                  <p className="text-purple-200 text-xs">Your daily priorities, ranked by AI</p>
                </div>
              </div>
              <span className="px-3 py-1 rounded-full bg-purple-500/20 border border-purple-400/40 text-purple-200 text-xs font-medium">LIVE</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Top overdue tenants */}
              <div
                data-testid="insight-overdue"
                onClick={() => navigate('/manager/money/dues')}
                className="group rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-4 hover:bg-white/15 transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="text-red-400" size={18} />
                    <span className="text-xs font-semibold text-red-200 uppercase tracking-wider">Chase Today</span>
                  </div>
                  <ArrowRight className="text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" size={16} />
                </div>
                <p className="text-3xl font-bold text-white mb-2">{insights.top_overdue_tenants?.length || 0}</p>
                <p className="text-xs text-purple-200 mb-3">Overdue tenants</p>
                <div className="space-y-1.5 max-h-20 overflow-hidden">
                  {(insights.top_overdue_tenants || []).slice(0, 3).map(t => (
                    <div key={t.id} className="text-xs text-white/80 flex items-center justify-between">
                      <span className="truncate">• {t.name}</span>
                      <span className="text-red-300 font-semibold ml-2">₹{(t.rent_amount || 0).toLocaleString()}</span>
                    </div>
                  ))}
                  {(!insights.top_overdue_tenants || insights.top_overdue_tenants.length === 0) && (
                    <p className="text-xs text-green-300 italic">All clear 🎉</p>
                  )}
                </div>
              </div>

              {/* Hot leads */}
              <div
                data-testid="insight-hot-leads"
                onClick={() => navigate('/manager/people/leads')}
                className="group rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-4 hover:bg-white/15 transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Flame className="text-orange-400" size={18} />
                    <span className="text-xs font-semibold text-orange-200 uppercase tracking-wider">Call Today</span>
                  </div>
                  <ArrowRight className="text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" size={16} />
                </div>
                <p className="text-3xl font-bold text-white mb-2">{insights.top_hot_leads?.length || 0}</p>
                <p className="text-xs text-purple-200 mb-3">Top scored leads</p>
                <div className="space-y-1.5 max-h-20 overflow-hidden">
                  {(insights.top_hot_leads || []).slice(0, 3).map(l => (
                    <div key={l.id} className="text-xs text-white/80 flex items-center justify-between">
                      <span className="truncate">• {l.name}</span>
                      <span className={`font-semibold ml-2 px-1.5 py-0.5 rounded ${
                        l.ai_label === 'hot' ? 'bg-red-500/30 text-red-200' :
                        l.ai_label === 'warm' ? 'bg-orange-500/30 text-orange-200' :
                        'bg-blue-500/30 text-blue-200'
                      }`}>{l.ai_score}</span>
                    </div>
                  ))}
                  {(!insights.top_hot_leads || insights.top_hot_leads.length === 0) && (
                    <p className="text-xs text-white/60 italic">No AI-scored leads yet. Run "AI Bulk Score" on leads page.</p>
                  )}
                </div>
              </div>

              {/* Urgent complaints */}
              <div
                data-testid="insight-urgent-complaints"
                onClick={() => navigate('/manager/complaints')}
                className="group rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-4 hover:bg-white/15 transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="text-red-400" size={18} />
                    <span className="text-xs font-semibold text-red-200 uppercase tracking-wider">Resolve Now</span>
                  </div>
                  <ArrowRight className="text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" size={16} />
                </div>
                <p className="text-3xl font-bold text-white mb-2">{insights.urgent_complaints || 0}</p>
                <p className="text-xs text-purple-200 mb-3">Urgent complaints open</p>
                <div className="mt-1">
                  {(insights.urgent_complaints || 0) === 0 ? (
                    <p className="text-xs text-green-300 italic">No fires today 🔥</p>
                  ) : (
                    <p className="text-xs text-white/80">Tap to view & assign →</p>
                  )}
                </div>
              </div>

              {/* Vacant rooms opportunity */}
              <div
                data-testid="insight-vacant-rooms"
                onClick={() => navigate('/manager/property/rooms')}
                className="group rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-4 hover:bg-white/15 transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <HomeIcon className="text-green-400" size={18} />
                    <span className="text-xs font-semibold text-green-200 uppercase tracking-wider">Opportunity</span>
                  </div>
                  <ArrowRight className="text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" size={16} />
                </div>
                <p className="text-3xl font-bold text-white mb-2">{insights.vacant_rooms || 0}</p>
                <p className="text-xs text-purple-200 mb-3">Vacant rooms</p>
                <p className="text-xs text-white/70 italic">
                  {(insights.vacant_rooms || 0) > 0
                    ? 'Convert bookings / list online'
                    : 'Fully occupied 💯'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Lead Funnel KPI (last 30 days) ── */}
      {funnel && funnel.total > 0 && (
        <Card
          data-testid="home-funnel-card"
          className="p-5 mb-6 border-0 bg-gradient-to-br from-indigo-600 via-purple-700 to-pink-700 text-white shadow-xl relative overflow-hidden"
        >
          <div className="absolute -right-10 -top-10 opacity-10 text-[160px] leading-none select-none">📈</div>
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1 text-[10px] font-semibold tracking-wider uppercase">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Last 30 days
              </div>
              <h3 className="text-xl font-bold mt-2">Lead → Tenant funnel</h3>
              <p className="text-white/70 text-xs">
                {funnel.conversion_rate}% of leads booked · {funnel.move_in_rate}% moved in{funnel.avg_time_to_convert_hours > 0 ? ` · ${(funnel.avg_time_to_convert_hours / 24).toFixed(1)}d avg time-to-convert` : ''}
              </p>
            </div>
            <button
              onClick={() => navigate('/manager/people/leads')}
              className="text-xs bg-white/15 hover:bg-white/25 transition-colors rounded-full px-3 py-1.5 font-semibold"
              data-testid="home-funnel-go"
            >
              Go to leads →
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              { k: 'total',     label: 'Total',     value: funnel.total },
              { k: 'contacted', label: 'Contacted', value: funnel.contacted },
              { k: 'visited',   label: 'Visited',   value: funnel.visited },
              { k: 'converted', label: 'Booked',    value: funnel.converted },
              { k: 'moved_in',  label: 'Moved in',  value: funnel.moved_in },
            ].map(b => (
              <div
                key={b.k}
                data-testid={`home-funnel-${b.k}`}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10"
              >
                <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">{b.label}</p>
                <p className="text-2xl font-bold mt-0.5 tabular-nums">{b.value ?? 0}</p>
              </div>
            ))}
          </div>
          {funnel.lost > 0 && (
            <p className="text-[11px] text-white/60 mt-3">
              <b className="text-red-200">{funnel.lost} lost</b>
              {Object.keys(funnel.lost_breakdown || {}).length > 0 && (
                <> — top reason: <b>{Object.entries(funnel.lost_breakdown).sort((a,b)=>b[1]-a[1])[0][0].replace('_',' ')}</b></>
              )}
            </p>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Payments */}
        <Card className="p-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="text-green-600" size={20} />
              Recent Payments
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/manager/money/collections')}
              className="text-blue-600 hover:text-blue-700"
            >
              View All <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
          {recentPayments.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No recent payments</p>
          ) : (
            <div className="space-y-3">
              {recentPayments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">₹{payment.amount}</p>
                    <p className="text-sm text-gray-600">{payment.payment_type}</p>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full ${
                    payment.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {payment.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Pending Tasks/Alerts */}
        <Card className="p-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Bell className="text-orange-600" size={20} />
              Pending Tasks
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="text-blue-600 hover:text-blue-700"
            >
              View All <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
          {pendingTasks.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-gray-500">All caught up!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingTasks.map((task) => (
                <div key={task.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <AlertCircle className={`${
                    task.priority === 'high' ? 'text-red-500' : 'text-yellow-500'
                  } mt-0.5`} size={18} />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{task.title}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Priority: <span className={`font-semibold ${
                        task.priority === 'high' ? 'text-red-600' : 'text-yellow-600'
                      }`}>{task.priority}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button
            onClick={() => navigate('/manager/property/list')}
            variant="outline"
            className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-blue-50 hover:border-blue-500"
          >
            <Building2 className="text-blue-600" size={24} />
            <span className="text-sm">Add Property</span>
          </Button>
          <Button
            onClick={() => navigate('/manager/people/tenants')}
            variant="outline"
            className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-purple-50 hover:border-purple-500"
          >
            <Users className="text-purple-600" size={24} />
            <span className="text-sm">Add Tenant</span>
          </Button>
          <Button
            onClick={() => navigate('/manager/money')}
            variant="outline"
            className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-green-50 hover:border-green-500"
          >
            <IndianRupee className="text-green-600" size={24} />
            <span className="text-sm">Record Payment</span>
          </Button>
          <Button
            onClick={() => navigate('/manager/dashboard')}
            variant="outline"
            className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-orange-50 hover:border-orange-500"
          >
            <TrendingUp className="text-orange-600" size={24} />
            <span className="text-sm">View Analytics</span>
          </Button>
        </div>
      </Card>
    </div>
  );
};

// ====================================================================
// Month Summary Strip — 6 visible KPI tiles + "More" expander (4 more)
// Mobile: horizontal scroll with snap points + position dots.
// ====================================================================
const MonthSummaryStrip = ({
  monthLabel,
  summary,
  showMore,
  onToggle,
  propertiesCount,
  hideTest,
  onToggleTest,
  isSuperAdmin,
}) => {
  const navigate = useNavigate();
  const tiles = showMore ? KPI_TILES : KPI_TILES.filter(t => !t.hidden);
  const scrollRef = React.useRef(null);
  const [activeDot, setActiveDot] = useState(0);

  // Click handler — navigates to the tile's mapped route and fires an
  // analytics event so we can measure which KPI is the most clicked
  // (helps us decide which deserve dedicated drill-down pages).
  const goToTile = (tile) => {
    if (!tile.route) return;
    track('kpi_tile_clicked', { kpi: tile.key, surface: 'home' });
    navigate(`${tile.route}${tile.query || ''}`);
  };

  // Mobile dot indicator — track which tile is mostly in view
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const tileWidth = 162; // 150px card + 12px gap
      const idx = Math.round(el.scrollLeft / tileWidth);
      setActiveDot(Math.min(tiles.length - 1, Math.max(0, idx)));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [tiles.length]);

  const toneClass = (tone) => ({
    success: 'text-green-600',
    danger:  'text-red-600',
    warn:    'text-orange-600',
  }[tone] || 'text-gray-900');
  const toneIconBg = (tone) => ({
    success: 'bg-green-50 text-green-600',
    danger:  'bg-red-50 text-red-600',
    warn:    'bg-orange-50 text-orange-600',
  }[tone] || 'bg-gray-100 text-gray-600');

  return (
    <div data-testid="month-summary-strip">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm flex-wrap">
          <span className="font-semibold text-gray-900">{monthLabel} summary</span>
          <span className="text-gray-500">for</span>
          <span className="px-2 py-0.5 rounded-md bg-orange-50 text-orange-700 text-xs font-medium border border-orange-200">
            All {propertiesCount} {propertiesCount === 1 ? 'Property' : 'Properties'}
          </span>
          {/* Hide test toggle — super_admin only (clients never see this) */}
          {isSuperAdmin && (
            <button
              onClick={onToggleTest}
              data-testid="toggle-hide-test"
              title={hideTest ? 'Test tenants are hidden from these numbers' : 'Test tenants are included'}
              className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md border transition-colors ${
                hideTest
                  ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                  : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${hideTest ? 'bg-green-500' : 'bg-gray-400'}`} />
              {hideTest ? 'Test data hidden' : 'Test data shown'}
            </button>
          )}
        </div>
        <button
          onClick={onToggle}
          data-testid="month-summary-more-btn"
          className="flex items-center gap-1 text-xs font-medium text-orange-700 hover:text-orange-800 px-2 py-1 rounded-md hover:bg-orange-50"
        >
          {showMore ? 'Less' : 'More'} {showMore ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Horizontal scroll on mobile with snap, grid on desktop */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth lg:grid lg:grid-cols-6 lg:overflow-visible xl:grid-cols-6 scrollbar-hide"
      >
        {tiles.map((tile) => {
          const Icon = tile.icon;
          const value = summary?.[tile.key] ?? 0;
          const display = tile.kind === 'money' ? inr(value) : Number(value).toLocaleString('en-IN');
          const clickable = Boolean(tile.route);
          return (
            <Card
              key={tile.key}
              data-testid={`kpi-${tile.key}`}
              onClick={clickable ? () => goToTile(tile) : undefined}
              onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToTile(tile); } } : undefined}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-label={clickable ? `${tile.label} — open ${tile.route.split('/').pop()}` : undefined}
              className={`p-3 min-w-[150px] lg:min-w-0 snap-start shadow-sm transition-all border border-gray-100 ${
                clickable
                  ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-gray-300 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-orange-400'
                  : 'hover:shadow-md'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <p className={`text-xl font-bold tabular-nums leading-none ${toneClass(tile.tone)}`}>{display}</p>
                <span className={`w-7 h-7 rounded-md flex items-center justify-center ${toneIconBg(tile.tone)}`}>
                  <Icon size={14} />
                </span>
              </div>
              <p className="text-[11px] text-gray-600 font-medium leading-tight">{tile.label}</p>
            </Card>
          );
        })}
      </div>

      {/* Mobile position dots — hidden on desktop where tiles are always all visible */}
      <div className="flex justify-center gap-1.5 mt-2 lg:hidden" data-testid="month-summary-dots">
        {tiles.map((t, i) => (
          <span
            key={t.key}
            className={`h-1.5 rounded-full transition-all ${
              i === activeDot ? 'w-5 bg-orange-600' : 'w-1.5 bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// ====================================================================
// Property Breakdown Strip — RentOk-style metric list per property
// ====================================================================
const PROPERTY_METRICS = [
  { key: 'occupied_beds',    label: 'Occupied Beds',    icon: BedDouble,     tone: 'success' },
  { key: 'active_tenants',   label: 'Active Tenants',   icon: Users,         tone: 'neutral' },
  { key: 'under_notice',     label: 'Under Notice',     icon: CalendarClock, tone: 'warn' },
  { key: 'pending_dues',     label: 'Pending Dues',     icon: AlertCircle,   tone: 'danger', kind: 'money' },
  { key: 'month_collection', label: 'Collection',       icon: Coins,         tone: 'success', kind: 'money' },
  { key: 'vacant_beds',      label: 'Vacant Beds',      icon: HomeIcon,      tone: 'danger' },
  { key: 'new_bookings',     label: 'New Bookings',     icon: Plus,          tone: 'neutral' },
  { key: 'total_leads',      label: 'Total Leads',      icon: Flame,         tone: 'neutral' },
  { key: 'tenants_with_dues',label: 'Tenants With Dues',icon: UserMinus,     tone: 'danger' },
  { key: 'active_complaints',label: 'Active Complaints',icon: MessageCircle, tone: 'danger' },
];

const PropertyBreakdownStrip = ({ properties, navigate }) => {
  return (
    <div data-testid="property-breakdown-strip">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-gray-900">Properties Overview</h2>
        <Button
          onClick={() => navigate('/manager/property/list')}
          variant="ghost"
          size="sm"
          className="text-orange-700 hover:text-orange-800"
        >
          View all <ArrowRight size={14} className="ml-1" />
        </Button>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3 snap-x snap-mandatory">
        {properties.map(p => (
          <PropertyCard key={p.id} prop={p} navigate={navigate} />
        ))}
      </div>
    </div>
  );
};

const PropertyCard = ({ prop, navigate }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? PROPERTY_METRICS : PROPERTY_METRICS.slice(0, 5);

  const copyCode = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(prop.code);
    toast({ title: 'Property code copied', description: prop.code });
  };

  const toneText = (tone) => {
    switch (tone) {
      case 'success': return 'text-green-600';
      case 'danger':  return 'text-red-600';
      case 'warn':    return 'text-orange-600';
      default:        return 'text-gray-900';
    }
  };
  const toneIconBg = (tone) => {
    switch (tone) {
      case 'success': return 'bg-green-50 text-green-600';
      case 'danger':  return 'bg-red-50 text-red-600';
      case 'warn':    return 'bg-orange-50 text-orange-600';
      default:        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <Card
      data-testid={`property-card-${prop.id}`}
      className="p-4 shadow-md min-w-[280px] max-w-[320px] flex-shrink-0 snap-start"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-gray-900 truncate">{prop.name}</h3>
          {prop.address && <p className="text-xs text-gray-500 truncate mt-0.5">{prop.address}</p>}
        </div>
        <span className="px-2 py-0.5 rounded-md bg-orange-50 text-orange-700 text-[10px] font-semibold border border-orange-200 whitespace-nowrap">
          Current
        </span>
      </div>

      {/* Rooms / Beds badge */}
      <div className="flex items-center justify-between bg-gradient-to-r from-orange-50 to-orange-100/50 rounded-lg p-2 mb-3">
        <div className="text-center flex-1">
          <p className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold">Rooms</p>
          <p className="text-lg font-bold text-gray-900 leading-none">{prop.rooms}</p>
        </div>
        <div className="w-9 h-9 rounded-full bg-orange-600 flex items-center justify-center shadow-sm flex-shrink-0">
          <Building2 className="text-white" size={16} />
        </div>
        <div className="text-center flex-1">
          <p className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold">Beds</p>
          <p className="text-lg font-bold text-gray-900 leading-none">{prop.beds}</p>
        </div>
      </div>

      {/* Code */}
      <button
        onClick={copyCode}
        className="w-full flex items-center justify-between text-xs text-gray-600 hover:text-orange-700 px-2 py-1.5 rounded-md hover:bg-orange-50 mb-2"
        data-testid={`property-code-${prop.id}`}
      >
        <span className="font-mono">{prop.code}</span>
        <Copy size={12} />
      </button>

      {/* Metrics */}
      <div className="space-y-1.5">
        {visible.map(m => {
          const Icon = m.icon;
          const v = prop.metrics?.[m.key] ?? 0;
          const display = m.kind === 'money' ? inr(v) : Number(v).toLocaleString('en-IN');
          return (
            <div key={m.key} className="flex items-center justify-between py-1 text-sm">
              <div className="flex items-center gap-2">
                <span className={`w-6 h-6 rounded-md flex items-center justify-center ${toneIconBg(m.tone)}`}>
                  <Icon size={12} />
                </span>
                <span className="text-gray-700">{m.label}</span>
              </div>
              <span className={`font-bold tabular-nums ${toneText(m.tone)}`}>{display}</span>
            </div>
          );
        })}
      </div>

      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full mt-2 text-center text-xs font-medium text-orange-700 hover:text-orange-800 py-1"
        data-testid={`property-expand-${prop.id}`}
      >
        {expanded ? (<>View Less <ChevronUp size={12} className="inline" /></>) : (<>View More <ChevronDown size={12} className="inline" /></>)}
      </button>

      {/* Footer action */}
      <Button
        onClick={() => navigate(`/manager/properties/${prop.id}`)}
        className="w-full mt-2 bg-orange-600 hover:bg-orange-700 text-white text-sm h-9"
        data-testid={`property-open-${prop.id}`}
      >
        Open Property
      </Button>
    </Card>
  );
};

export default Home;
